import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  AppCard,
  AppModal,
  AppMultiSelect,
  AppNoInfo,
  AppNotificationAlert,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import errorHandler from "@/layout/components/error/ErrorHandler";
import { axiosPath, path } from "../../../routing/helpers/router";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import TotalEnergiesRiskSidebar from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar";
import AppLink from "../../../routing/helpers/AppLink";
import TotalEnergiesRiskProcessSelect from "../../../layout/components/form/Selects/TotalEnergiesRiskProcessSelect";
import {
  getImpactGroupType,
  getInherentImpact,
  getProbability,
  getRiskState,
  getRiskType,
  getIf1,
  getIf2,
  getIo1,
  getIo2,
  getIl1,
  getIl2,
  getIl3,
  getIr1,
  getIr2,
  getIr3,
  getIp1,
  getIp2,
  getTotal,
} from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/tools/totalEnergiesRiskRiskTools";
import TotalEnergiesRiskRiskSelect from "../../../layout/components/form/Selects/TotalEnergiesRiskRiskSelect";
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskRiskHomeScreen() {
  const { moduleBridgeHex, riskHex, filtersHex, page } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const permissionLevel = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedImpactGroupTypes, setSelectedImpactGroupTypes] = useState([]);
  const [
    assessmentAssistantInherentImpact,
    setAssessmentAssistantInherentImpact,
  ] = useState();
  const [showAssistantModal, setShowAssistantModal] = useState();
  const [risk, setRisk] = useState();
  const [riskTypeOptions] = useState(
    Array.from({ length: 8 }, (_, i) => ({ value: i, label: getRiskType(i) })),
  );
  const [stateOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({ value: i, label: getRiskState(i) })),
  );
  const [probabilityOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getProbability(i),
    })),
  );
  const [inherentImpactOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getInherentImpact(i),
    })),
  );
  const [impactGroupTypeOptions] = useState(
    Array.from({ length: 7 }, (_, i) => ({
      value: i,
      label: getImpactGroupType(i),
    })),
  );
  const [if1Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIf1(i),
    })),
  );
  const [if2Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIf2(i),
    })),
  );
  const [il1Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIl1(i),
    })),
  );
  const [il2Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIl2(i),
    })),
  );
  const [il3Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIl3(i),
    })),
  );
  const [io1Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIo1(i),
    })),
  );
  const [io2Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIo2(i),
    })),
  );
  const [ir1Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIr1(i),
    })),
  );
  const [ir2Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIr2(i),
    })),
  );
  const [ir3Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIr3(i),
    })),
  );
  const [ip1Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIp1(i),
    })),
  );
  const [ip2Options] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getIp2(i),
    })),
  );
  const validationSchema = Yup.object().shape({
    description: Yup.string().required("Requerido"),
    riskType: Yup.number()
      .nullable()
      .when("state", {
        is: (v) => v === 5,
        then: (s) => s.required("Requerido"),
        otherwise: (s) => s.notRequired(),
      }),
    subProcess: Yup.number().nullable().required("Requerido"),
    impactGroupTypes: Yup.array()
      .of(Yup.string().required("Requerido"))
      .when("state", {
        is: (v) => v === 5,
        then: (s) => s.min(1, "Requerido"),
        otherwise: (s) => s.notRequired(),
      }),
    probability: Yup.number()
      .nullable()
      .when("state", {
        is: (v) => v === 5,
        then: (s) => s.required("Requerido"),
        otherwise: (s) => s.notRequired(),
      }),
    inherentImpact: Yup.number()
      .nullable()
      .when("state", {
        is: (v) => v === 5,
        then: (s) => s.required("Requerido"),
        otherwise: (s) => s.notRequired(),
      }),
  });
  const deleteValidationSchema = Yup.object().shape({
    riskID: Yup.number()
      .nullable()
      .when([], {
        is: () => risk?.controlsCount > 0 || risk?.actionPlansCount,
        then: (s) => s.required("Requerido"),
        otherwise: (s) => s.notRequired(),
      }),
  });

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD RISK
  const loadRisk = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_totalEnergiesRisk_risk_get"), {
          moduleBridgeHex,
          riskHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setRisk(data);
            setAssessmentAssistantInherentImpact(
              data?.assessmentAssistantInherentImpact,
            );
            setSelectedImpactGroupTypes(data?.impactGroupTypes);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, riskHex],
  );

  useEffect(() => {
    if (riskHex) {
      setLoading(true);
      loadRisk();
    }
  }, []);
  // < LOAD RISK

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            riskHex
              ? "api_r_user_module_totalEnergiesRisk_risk_edit"
              : "api_r_user_module_totalEnergiesRisk_risk_add",
          ),
          {
            moduleBridgeHex,
            riskHex,
            ...values,
          },
        )
        .then(() => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: "Completado",
              message: riskHex
                ? "Riesgo editado con éxito"
                : "Riesgo creado con éxito",
              type: "success",
              duration: 3000,
            });
            if (!riskHex) {
              navigate(
                path({
                  name: "user_module_totalEnergiesRisk_risk_list",
                  params: { moduleBridgeHex, page: 1, filtersHex: 0 },
                }),
              );
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, riskHex],
  );
  // < SAVE

  // > ASSISTANT
  const handleSaveAssistant = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_totalEnergiesRisk_risk_assessmentAssistantInherentImpact_edit",
          ),
          {
            moduleBridgeHex,
            riskHex,
            ...values,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setAssessmentAssistantInherentImpact(data);
            AppNotificationAlert({
              title: "Completado",
              message: "Asistente de evaluación de impacto guardado",
              type: "success",
              duration: 3000,
            });
            setShowAssistantModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, riskHex],
  );
  // < ASSISTANT

  // > CANCEL
  const handleCancel = useCallback(
    async (values) =>
      axios
        .post(axiosPath("api_r_user_module_totalEnergiesRisk_risk_cancel"), {
          moduleBridgeHex,
          riskHex,
          ...values,
        })
        .then(() => {
          if (isMounted.current) {
            setRisk({
              ...risk,
              active: false,
              controlsCount: 0,
              actionPlansCount: 0,
            });
            setShowDeleteModal(false);
            AppNotificationAlert({
              title: "Completado",
              message: "El Riesgo ha sido desactivado",
              type: "success",
              duration: 5000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, riskHex, risk],
  );
  // < CANCEL

  // > REACTIVATE
  const handleReactivate = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_risk_reactivate"),
          {
            moduleBridgeHex,
            riskHex,
          },
        )
        .then(() => {
          if (isMounted.current) {
            setRisk({ ...risk, active: true });
            AppNotificationAlert({
              title: "Completado",
              message: "El Riesgo ha sido reactivado",
              type: "success",
              duration: 5000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, risk, riskHex],
  );
  // < REACTIVATE

  const getTotalInherentImpact = useCallback(
    (values) => {
      let totalValues = [];

      if (selectedImpactGroupTypes.includes(0)) {
        totalValues = [...totalValues, values?.if1, values?.if2];
      }
      if (selectedImpactGroupTypes.includes(1)) {
        totalValues = [...totalValues, values?.il1, values?.il2, values?.il3];
      }
      if (selectedImpactGroupTypes.includes(2)) {
        totalValues = [...totalValues, values?.ir1, values?.ir2, values?.ir3];
      }
      if (selectedImpactGroupTypes.includes(3)) {
        totalValues = [...totalValues, values?.io1, values?.io2];
      }
      if (selectedImpactGroupTypes.includes(5)) {
        totalValues = [...totalValues, values?.ip1, values?.ip2];
      }

      if (totalValues?.length > 0 && totalValues.some((v) => v !== null)) {
        return (
          <div>
            <b>Impacto Inherente: {getTotal(Math.max(...totalValues))}</b>
          </div>
        );
      }

      return null;
    },
    [getTotal, selectedImpactGroupTypes],
  );

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
        "moduleData",
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "permissions",
          {
            moduleBridgeHex,
            subType: "permissions",
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "userBridge",
          {
            moduleBridgeHex,
            subType: "userBridge",
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />;

  return (
    <>
      <TotalEnergiesRiskSidebar />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: "user_module_totalEnergiesRisk_risk_list",
                    params: {
                      moduleBridgeHex,
                      page,
                      filtersHex,
                    },
                  },
                },
                { name: riskHex ? "Edición de Riesgo" : "Nuevo Riesgo" },
              ]
            : [{ name: "..." }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              initialValues={{
                description: risk?.description || "",
                causes: risk?.causes || "",
                consequences: risk?.consequences || "",
                subProcess: risk?.subProcess ?? null,
                riskType: risk?.riskType ?? null,
                state: risk?.state ?? 0,
                impactGroupTypes: risk?.impactGroupTypes ?? [],
                probability: risk?.probability ?? null,
                inherentImpact: risk?.inherentImpact ?? null,
              }}
              onSubmit={handleSave}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <AppCard
                    header={
                      <div>
                        {riskHex ? "Edición de riesgo" : "Nuevo riesgo"}
                        {riskHex && (
                          <button
                            type="button"
                            className={`btn btn-xs btn-${
                              risk?.active ? "danger" : "success"
                            } float-right`}
                            onClick={() =>
                              risk?.active
                                ? setShowDeleteModal(true)
                                : handleReactivate()
                            }
                          >
                            {risk?.active ? "Desactivar" : "Activar"}
                          </button>
                        )}
                      </div>
                    }
                  >
                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-lg-12">
                          <AppTextArea
                            title="Descripción"
                            onChange={(value) =>
                              setFieldValue("description", value)
                            }
                            value={values.description}
                            error={errors.description}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Causas"
                            onChange={(value) => setFieldValue("causes", value)}
                            value={values.causes}
                            error={errors.causes}
                            isDisabled={permissionLevel !== 0}
                            required={values?.state === 5}
                            isClearable
                            resize
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Consecuencias"
                            onChange={(value) =>
                              setFieldValue("consequences", value)
                            }
                            value={values.consequences}
                            error={errors.consequences}
                            isDisabled={permissionLevel !== 0}
                            required={values?.state === 5}
                            isClearable
                            resize
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-6">
                          <TotalEnergiesRiskProcessSelect
                            value={values?.subProcess}
                            error={errors?.subProcess}
                            onHandleChange={(opt) => {
                              setFieldValue("subProcess", opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            hasSearchBox
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={riskTypeOptions}
                            title="Tipo de riesgo"
                            value={values?.riskType}
                            error={errors?.riskType}
                            onChange={(opt) => {
                              setFieldValue("riskType", opt);
                            }}
                            isClearable
                            required={values?.state === 5}
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppMultiSelect
                            options={impactGroupTypeOptions}
                            title="Tipos de impacto"
                            value={values?.impactGroupTypes}
                            error={errors?.impactGroupTypes}
                            onChange={(opt) => {
                              setFieldValue("impactGroupTypes", opt);
                              setSelectedImpactGroupTypes(opt);
                            }}
                            hasSelectOptions
                            required={values?.state === 5}
                          />
                        </div>
                        <div className="col-lg-6 d-flex">
                          <AppSingleSelect
                            options={inherentImpactOptions}
                            title="Impacto inherente"
                            value={values?.inherentImpact}
                            error={errors?.inherentImpact}
                            onChange={(opt) => {
                              setFieldValue("inherentImpact", opt);
                            }}
                            isClearable
                            required={values?.state === 5}
                          />
                          {riskHex && (
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-light ml-2"
                              style={{ margin: "10px 0px" }}
                              onClick={() => setShowAssistantModal(true)}
                            >
                              Asistente de evaluación de impacto
                            </button>
                          )}
                        </div>
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={probabilityOptions}
                            title="Probabilidad"
                            value={values?.probability}
                            error={errors?.probability}
                            onChange={(opt) => {
                              setFieldValue("probability", opt);
                            }}
                            required={values?.state === 5}
                            isClearable
                          />
                        </div>
                        {riskHex && (
                          <div className="col-lg-6">
                            <AppSingleSelect
                              options={stateOptions}
                              title="Estado de riesgo"
                              value={values?.state}
                              error={errors?.state}
                              onChange={(opt) => {
                                setFieldValue("state", opt);
                              }}
                              required
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </AppCard>
                  <div className="row mt-3">
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-sm btn-success float-right"
                        disabled={isSubmitting || (risk && !risk?.active)}
                      >
                        {riskHex ? "Editar" : "Crear"}
                      </button>
                      <AppLink
                        path="user_module_totalEnergiesRisk_risk_list"
                        params={{
                          moduleBridgeHex,
                          page,
                          filtersHex,
                        }}
                        className="btn btn-sm btn-outline-danger float-right mr-2"
                      >
                        Volver
                      </AppLink>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </main>
      <AppModal
        show={showDeleteModal}
        title="Desactivar Riesgo"
        onClose={() => setShowDeleteModal(false)}
      >
        <Formik
          initialValues={{
            riskID: null,
          }}
          validationSchema={deleteValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleCancel}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              {(risk?.controlsCount > 0 || risk?.actionPlansCount) > 0 && (
                <>
                  <p>
                    Selecciona un riesgo al que asignar los controles y planes
                    de acción de este riesgo antes de desactivarlo. Los
                    controles con riesgos secundarios no seran reasignados al
                    riesgo seleccionado.
                  </p>
                  <TotalEnergiesRiskRiskSelect
                    title="Riesgos"
                    value={values?.riskID}
                    error={errors?.riskID}
                    onHandleChange={(opt) => setFieldValue("riskID", opt)}
                    hasSearchBox
                    isClearable
                    filterHex={riskHex}
                  />
                </>
              )}
              <button
                type="submit"
                className="btn btn-xs btn-danger float-right"
                disabled={isSubmitting}
              >
                Desactivar
              </button>
              <div className="clearfix" />
            </form>
          )}
        </Formik>
      </AppModal>
      {riskHex && (
        <AppModal
          show={showAssistantModal}
          title="Asistente de evaluación de impacto"
          onClose={() => setShowAssistantModal(false)}
        >
          <Formik
            initialValues={{
              if1: assessmentAssistantInherentImpact?.if1 ?? null,
              if2: assessmentAssistantInherentImpact?.if2 ?? null,
              il1: assessmentAssistantInherentImpact?.il1 ?? null,
              il2: assessmentAssistantInherentImpact?.il2 ?? null,
              il3: assessmentAssistantInherentImpact?.il3 ?? null,
              io1: assessmentAssistantInherentImpact?.io1 ?? null,
              io2: assessmentAssistantInherentImpact?.io2 ?? null,
              ir1: assessmentAssistantInherentImpact?.ir1 ?? null,
              ir2: assessmentAssistantInherentImpact?.ir2 ?? null,
              ir3: assessmentAssistantInherentImpact?.ir3 ?? null,
              ip1: assessmentAssistantInherentImpact?.ip1 ?? null,
              ip2: assessmentAssistantInherentImpact?.ip2 ?? null,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSaveAssistant}
          >
            {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                {selectedImpactGroupTypes.includes(0) && (
                  <>
                    <h6 className="mb-3">{getImpactGroupType(0)}</h6>
                    <AppSingleSelect
                      title="Impacto Financiero 1"
                      options={if1Options}
                      value={values?.if1}
                      onChange={(opt) => setFieldValue("if1", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Financiero 2"
                      options={if2Options}
                      value={values?.if2}
                      onChange={(opt) => setFieldValue("if2", opt)}
                      isClearable
                    />
                  </>
                )}
                {selectedImpactGroupTypes.includes(1) && (
                  <>
                    <h6 className="my-3">{getImpactGroupType(1)}</h6>
                    <AppSingleSelect
                      title="Impacto Legal 1"
                      options={il1Options}
                      value={values?.il1}
                      onChange={(opt) => setFieldValue("il1", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Legal 2"
                      options={il2Options}
                      value={values?.il2}
                      onChange={(opt) => setFieldValue("il2", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Legal 3"
                      options={il3Options}
                      value={values?.il3}
                      onChange={(opt) => setFieldValue("il3", opt)}
                      isClearable
                    />
                  </>
                )}
                {selectedImpactGroupTypes.includes(2) && (
                  <>
                    <h6 className="my-3">{getImpactGroupType(2)}</h6>
                    <AppSingleSelect
                      title="Impacto Reputacional 1"
                      options={ir1Options}
                      value={values?.ir1}
                      onChange={(opt) => setFieldValue("ir1", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Reputacional 2"
                      options={ir2Options}
                      value={values?.ir2}
                      onChange={(opt) => setFieldValue("ir2", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Reputacional 3"
                      options={ir3Options}
                      value={values?.ir3}
                      onChange={(opt) => setFieldValue("ir3", opt)}
                      isClearable
                    />
                  </>
                )}
                {selectedImpactGroupTypes.includes(3) && (
                  <>
                    <h6 className="my-3">{getImpactGroupType(3)}</h6>
                    <AppSingleSelect
                      title="Impacto Operacional 1"
                      options={io1Options}
                      value={values?.io1}
                      onChange={(opt) => setFieldValue("io1", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Operacional 2"
                      options={io2Options}
                      value={values?.io2}
                      onChange={(opt) => setFieldValue("io2", opt)}
                      isClearable
                    />
                  </>
                )}
                {selectedImpactGroupTypes.includes(5) && (
                  <>
                    <h6 className="my-3">{getImpactGroupType(5)}</h6>
                    <AppSingleSelect
                      title="Impacto Personas/RRHH 1"
                      options={ip1Options}
                      value={values?.ip1}
                      onChange={(opt) => setFieldValue("ip1", opt)}
                      isClearable
                    />
                    <AppSingleSelect
                      title="Impacto Personas/RRHH 2"
                      options={ip2Options}
                      value={values?.ip2}
                      onChange={(opt) => setFieldValue("ip2", opt)}
                      isClearable
                    />
                  </>
                )}
                {selectedImpactGroupTypes?.length > 0 ? (
                  <>
                    {getTotalInherentImpact(values)}
                    <button
                      type="submit"
                      className="btn btn-xs btn-success float-right"
                      disabled={isSubmitting}
                    >
                      Guardar
                    </button>
                    <div className="clearfix" />
                  </>
                ) : (
                  <p>Selecciona un impacto grupo</p>
                )}
              </form>
            )}
          </Formik>
        </AppModal>
      )}
    </>
  );
}
